import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { getEntityTypeColor } from './FilterPanel';

const NODE_COLORS = {
  Person: '#f97316',
  AbstractConcept: '#8b5cf6',
  ContentAtom: '#3b82f6',
  HeartbeatInsight: '#10b981',
  Primitive: '#ec4899',
  Tension: '#f59e0b',
};

function getNodeColor(nodeType) {
  return NODE_COLORS[nodeType] || getEntityTypeColor(nodeType);
}

export default function GraphCanvas({ data, onNodeClick, onLinkClick, selectedNodeId, selectedLinkId, filters }) {
  const fgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredLinkId, setHoveredLinkId] = useState(null);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        setDimensions({ width, height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Filter nodes by active types
  const filteredData = useMemo(() => {
    const activeTypes = new Set(filters.nodeTypes);
    const visibleNodes = data.nodes.filter((n) => activeTypes.has(n.nodeType));
    const visibleIds = new Set(visibleNodes.map((n) => n.id));
    const visibleLinks = data.links.filter(
      (l) =>
        visibleIds.has(l.source?.id || l.source) &&
        visibleIds.has(l.target?.id || l.target)
    );
    return { nodes: visibleNodes, links: visibleLinks };
  }, [data, filters.nodeTypes]);

  // Build focused set — null when nothing selected, Set of node IDs when a node or link is selected
  const focusedIds = useMemo(() => {
    if (selectedNodeId) {
      const ids = new Set([selectedNodeId]);
      for (const link of filteredData.links) {
        const srcId = link.source?.id || link.source;
        const tgtId = link.target?.id || link.target;
        if (srcId === selectedNodeId) ids.add(tgtId);
        if (tgtId === selectedNodeId) ids.add(srcId);
      }
      return ids;
    }
    if (selectedLinkId) {
      const link = filteredData.links.find((l) => l.id === selectedLinkId);
      if (link) {
        return new Set([link.source?.id || link.source, link.target?.id || link.target]);
      }
    }
    return null;
  }, [selectedNodeId, selectedLinkId, filteredData.links]);

  // Custom node renderer
  const paintNode = useCallback(
    (node, ctx, globalScale) => {
      const label = node.label || '?';
      const isSelected = node.id === selectedNodeId;
      const isFocused = !focusedIds || focusedIds.has(node.id); // true when no selection or in neighbor set
      const isDimmed = focusedIds && !isFocused;
      const baseRadius = node.nodeType === 'Person' ? 8 : node.nodeType === 'Primitive' ? 7 : 5;
      const radius = isDimmed ? baseRadius * 0.6 : baseRadius;
      const color = getNodeColor(node.nodeType);

      ctx.globalAlpha = isDimmed ? 0.12 : 1;

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = isSelected ? '#ffffff' : color;
      ctx.fill();

      // Selection ring
      if (isSelected) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      // Label (only for focused nodes at reasonable zoom)
      if (!isDimmed) {
        const fontSize = Math.max(10 / globalScale, 2);
        if (globalScale > 0.4) {
          ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = 'rgba(220, 220, 230, 0.85)';
          ctx.fillText(label.slice(0, 24), node.x, node.y + radius + 2);
        }
      }

      ctx.globalAlpha = 1;
    },
    [selectedNodeId, selectedLinkId, focusedIds]
  );

  // Edge label renderer
  const paintLink = useCallback(
    (link, ctx, globalScale) => {
      if (!ctx || typeof ctx.beginPath !== 'function') return;
      const src = link.source;
      const tgt = link.target;
      if (typeof src !== 'object' || typeof tgt !== 'object') return;

      // Check if this edge is in the focused set
      const srcId = src.id || src;
      const tgtId = tgt.id || tgt;
      const isSelected = link.id === selectedLinkId;
      const isHovered = link.id === hoveredLinkId;
      const connectsToSelected = selectedNodeId && (srcId === selectedNodeId || tgtId === selectedNodeId);
      const isFocusedEdge = isSelected || connectsToSelected;
      const isDimmed = focusedIds && !isFocusedEdge;

      ctx.globalAlpha = isDimmed ? 0.04 : 1;

      // Draw the line — uniform width/opacity, state determines appearance
      ctx.beginPath();
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
      const hoverBoost = isHovered && !isSelected;
      ctx.strokeStyle = isSelected ? 'rgba(255,255,255,0.9)' : hoverBoost ? 'rgba(255,255,255,0.5)' : connectsToSelected ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)';
      ctx.lineWidth = isSelected ? 1.5 : hoverBoost ? 1.0 : 0.5;
      ctx.stroke();

      // Draw arrow
      const dx = tgt.x - src.x;
      const dy = tgt.y - src.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 1) { ctx.globalAlpha = 1; return; }
      const tgtRadius = tgt.nodeType === 'Person' ? 8 : tgt.nodeType === 'Primitive' ? 7 : 5;
      const arrowLen = 4;
      const endX = tgt.x - (dx / len) * (tgtRadius + 2);
      const endY = tgt.y - (dy / len) * (tgtRadius + 2);
      const angle = Math.atan2(dy, dx);

      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(endX - arrowLen * Math.cos(angle - Math.PI / 6), endY - arrowLen * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(endX - arrowLen * Math.cos(angle + Math.PI / 6), endY - arrowLen * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fillStyle = isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)';
      ctx.fill();

      // Edge label (only for focused edges at zoom > 0.6)
      if (!isDimmed && globalScale > 0.6 && link.type) {
        const midX = (src.x + tgt.x) / 2;
        const midY = (src.y + tgt.y) / 2;
        const fontSize = Math.max(7 / globalScale, 1.5);
        ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isSelected ? 'rgba(255,255,255,0.8)' : 'rgba(180,180,200,0.5)';
        ctx.fillText(link.type, midX, midY - 3 / globalScale);
      }

      ctx.globalAlpha = 1;
    },
    [selectedLinkId, selectedNodeId, focusedIds, hoveredLinkId]
  );

  // Link pointer area for click detection
  const linkPointerArea = useCallback((link, paintStyle, ctx) => {
    if (!ctx || typeof ctx.beginPath !== 'function') return;
    const src = link.source;
    const tgt = link.target;
    if (typeof src !== 'object' || typeof tgt !== 'object') return;

    const dx = tgt.x - src.x;
    const dy = tgt.y - src.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;

    // Perpendicular offset for hit area (6px each side = 12px total)
    const nx = -dy / len * 6;
    const ny = dx / len * 6;

    ctx.beginPath();
    ctx.moveTo(src.x + nx, src.y + ny);
    ctx.lineTo(tgt.x + nx, tgt.y + ny);
    ctx.lineTo(tgt.x - nx, tgt.y - ny);
    ctx.lineTo(src.x - nx, src.y - ny);
    ctx.closePath();
    ctx.fillStyle = paintStyle;
    ctx.fill();
  }, []);

  // Post-render overlay: repaint focused edges then focused nodes on top.
  // Z-order: dimmed content → focused edges → focused nodes.
  const onRenderFramePost = useCallback(
    (ctx, globalScale) => {
      if (!focusedIds) return; // no selection — skip overlay

      // Pass 1: Redraw focused edges on top of dimmed content
      for (const link of filteredData.links) {
        const src = link.source;
        const tgt = link.target;
        if (typeof src !== 'object' || typeof tgt !== 'object') continue;

        const srcId = src.id || src;
        const tgtId = tgt.id || tgt;
        const isSelected = link.id === selectedLinkId;
        const connectsToSelected = selectedNodeId && (srcId === selectedNodeId || tgtId === selectedNodeId);
        if (!isSelected && !connectsToSelected) continue;

        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(src.x, src.y);
        ctx.lineTo(tgt.x, tgt.y);
        ctx.strokeStyle = isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)';
        ctx.lineWidth = isSelected ? 1.5 : 0.5;
        ctx.stroke();

        // Arrow
        const dx = tgt.x - src.x;
        const dy = tgt.y - src.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) continue;
        const tgtRadius = tgt.nodeType === 'Person' ? 8 : tgt.nodeType === 'Primitive' ? 7 : 5;
        const arrowLen = 4;
        const endX = tgt.x - (dx / len) * (tgtRadius + 2);
        const endY = tgt.y - (dy / len) * (tgtRadius + 2);
        const angle = Math.atan2(dy, dx);

        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(endX - arrowLen * Math.cos(angle - Math.PI / 6), endY - arrowLen * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(endX - arrowLen * Math.cos(angle + Math.PI / 6), endY - arrowLen * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fillStyle = isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)';
        ctx.fill();
      }

      // Pass 2: Redraw focused nodes on top of edges so edges don't cross over nodes
      for (const node of filteredData.nodes) {
        if (!focusedIds.has(node.id)) continue;

        const isSelected = node.id === selectedNodeId;
        const baseRadius = node.nodeType === 'Person' ? 8 : node.nodeType === 'Primitive' ? 7 : 5;
        const color = getNodeColor(node.nodeType);

        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, baseRadius, 0, 2 * Math.PI);
        ctx.fillStyle = isSelected ? '#ffffff' : color;
        ctx.fill();

        if (isSelected) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }

        // Label
        const label = node.label || '?';
        if (globalScale > 0.4) {
          const fontSize = Math.max(10 / globalScale, 2);
          ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = 'rgba(220, 220, 230, 0.85)';
          ctx.fillText(label.slice(0, 24), node.x, node.y + baseRadius + 2);
        }
      }

      ctx.globalAlpha = 1;
    },
    [focusedIds, filteredData.links, filteredData.nodes, selectedLinkId, selectedNodeId]
  );

  // Configure d3 forces for better spacing
  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3Force('charge')?.strength(-45);
    fgRef.current.d3Force('link')?.distance(40);
  }, []);

  // Center camera on selected node (no pinning — just pan)
  useEffect(() => {
    if (!fgRef.current || !selectedNodeId) return;
    const node = filteredData.nodes.find((n) => n.id === selectedNodeId);
    if (!node || node.x === undefined) return;

    fgRef.current.centerAt(node.x, node.y, 400);
  }, [selectedNodeId, filteredData.nodes]);

  // Zoom to fit on initial data load
  useEffect(() => {
    if (fgRef.current && data.nodes.length > 0) {
      setTimeout(() => {
        fgRef.current.zoomToFit(400, 30);
      }, 500);
    }
  }, [data.nodes.length]);

  return (
    <div ref={containerRef} className="flex-1 h-full min-h-0">
      <ForceGraph2D
        ref={fgRef}
        graphData={filteredData}
        nodeCanvasObject={paintNode}
        nodePointerAreaPaint={(node, color, ctx) => {
          const radius = node.nodeType === 'Person' ? 10 : 7;
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.fill();
        }}
        onNodeClick={onNodeClick}
        linkCanvasObject={paintLink}
        linkCanvasObjectMode={() => 'replace'}
        linkPointerAreaPaint={linkPointerArea}
        onLinkHover={(link) => setHoveredLinkId(link?.id || null)}
        onLinkClick={onLinkClick}
        linkHoverPrecision={10}
        onRenderFramePost={onRenderFramePost}
        backgroundColor="transparent"
        width={dimensions.width}
        height={dimensions.height}
        d3AlphaDecay={0.03}
        d3VelocityDecay={0.3}
        cooldownTicks={150}
        warmupTicks={50}
      />
    </div>
  );
}

export { NODE_COLORS };
